import { AxiosRequestConfig } from 'axios';
import { EntityDefaultName } from '@hevoio/hevo-ui-toolkit';
import { GenericApiResponse } from '../../shared/interfaces/generics';
import { hermesConfig } from '../../request/hermes.config';
import {
  BACKGROUND_NETWORK_REQ_UI_OPTIONS,
  DEFAULT_NETWORK_REQ_OPTIONS,
  NETWORK_REQ_OPTIONS_WITH_TOAST
} from '../../request/constants/request-options';
import { hermesCourierService } from '../../request/services/hermes.courier.service';
import {
  RawIntegration,
  RawIntegrationBannerResponse,
  RawPipelineReviewContent,
  RawPipelineReviewSelectionResponse,
  StandardPipeline
} from '../interfaces/integration';
import { NetworkRequestOptions } from '../../request/interfaces/request-options';
import {
  DestinationPartitionKeyValidationRequest,
  DestinationPrefixValidationRequest,
  RawDestinationType
} from '../../destinations/interfaces/destination';
import { RawEntityDisplayConfig } from '../../shared/interfaces/entity-display-config';
import { RawSourceType } from '../../sources/interfaces/source';
import { PipelineConfigPayload } from '../PipelineSettings/interfaces/pipeline-settings-form-state';
import { PipelineSettingsFeaturesData } from '../interfaces/pipeline-settings-config';

export const PipelineAPI = {
  integrationsURL: hermesConfig.getIntegrationsURL(),
  stagedIntegrationsURL: hermesConfig.getStagedIntegrationsURL(),
  upgradeIntegrationURL: hermesConfig.getV1IntegrationURL(),
  objectsConfigURL: `${hermesConfig.getObjectsURL()}/configuration`,

  createIntegration(options = DEFAULT_NETWORK_REQ_OPTIONS) {
    const requestURL = `${this.integrationsURL}/create`;

    return hermesCourierService.post<
      GenericApiResponse<{ id: number; seq_id: number; team_id: number }>
    >(requestURL, {}, options);
  },

  getDefaultPipelineName(
    options = DEFAULT_NETWORK_REQ_OPTIONS
  ): Promise<GenericApiResponse<EntityDefaultName>> {
    const requestURL = `${this.integrationsURL}/default-name`;

    return hermesCourierService.get<GenericApiResponse<EntityDefaultName>>(requestURL, options);
  },

  getIntegration(integrationId: number, options = DEFAULT_NETWORK_REQ_OPTIONS) {
    const requestURL = `${this.integrationsURL}/${integrationId}`;

    return hermesCourierService.get<GenericApiResponse<RawIntegration>>(requestURL, options);
  },

  getStagedIntegration(integrationId: number, options = DEFAULT_NETWORK_REQ_OPTIONS) {
    const requestURL = `${this.stagedIntegrationsURL}/${integrationId}`;

    return hermesCourierService.get<GenericApiResponse<RawIntegration>>(requestURL, options);
  },

  getIntegrationStatus(integrationId: number) {
    const requestURL = `${this.integrationsURL}/${integrationId}/status?include_deleted=true`;

    return hermesCourierService.get<GenericApiResponse<RawIntegration['status']>>(requestURL);
  },

  getIntegrations(options = DEFAULT_NETWORK_REQ_OPTIONS) {
    const requestURL = this.integrationsURL;

    return hermesCourierService.get<GenericApiResponse<RawIntegration[]>>(requestURL, {
      ...options,
      networkOptions: {
        params: {
          include_deleted: false
        }
      }
    });
  },

  editIntegrationConfig(
    integrationId: number,
    config: PipelineConfigPayload,
    options = DEFAULT_NETWORK_REQ_OPTIONS
  ) {
    const requestURL = `${this.integrationsURL}/${integrationId}/edit/config`;

    return hermesCourierService.post<GenericApiResponse<RawIntegration>>(
      requestURL,
      config,
      options
    );
  },

  configureSource(integrationId: number, sourceId: number, options = DEFAULT_NETWORK_REQ_OPTIONS) {
    const requestURL = `${this.integrationsURL}/${integrationId}/edit/source`;
    const params = {
      source_id: sourceId
    };

    return hermesCourierService.post<GenericApiResponse<RawIntegration>>(
      requestURL,
      params,
      options
    );
  },

  configureDestination(
    integrationId: number,
    destinationId: number,
    options = DEFAULT_NETWORK_REQ_OPTIONS
  ) {
    const requestURL = `${this.integrationsURL}/${integrationId}/edit/destination`;
    const params = {
      destination_id: destinationId
    };

    return hermesCourierService.post<GenericApiResponse<RawIntegration>>(
      requestURL,
      params,
      options
    );
  },

  validateIntegrationName(pipelineName: string, config = {}, extraParams = {}) {
    const requestURL = `${this.integrationsURL}/validate/pipeline-name`;

    const options: NetworkRequestOptions = {
      uiOptions: BACKGROUND_NETWORK_REQ_UI_OPTIONS,
      networkOptions: {
        ...config
      }
    };

    const params = {
      pipeline_name: pipelineName,
      ...extraParams
    };

    return hermesCourierService.post<GenericApiResponse<{ valid: boolean; reason?: string }>>(
      requestURL,
      params,
      options
    );
  },

  validateDestinationPrefix(
    value: DestinationPrefixValidationRequest,
    config = {},
    extraParams = {}
  ) {
    const requestURL = `${this.integrationsURL}/validate/destination-prefix/${value.destinationType}`;

    const params = {
      destination_prefix: value.destinationPrefix,
      ...extraParams
    };

    const options: NetworkRequestOptions = {
      uiOptions: BACKGROUND_NETWORK_REQ_UI_OPTIONS,
      networkOptions: {
        ...config
      }
    };

    return hermesCourierService.post<GenericApiResponse<{ valid: boolean; reason: string }>>(
      requestURL,
      params,
      options
    );
  },

  validateDestinationPartitionKey(value: DestinationPartitionKeyValidationRequest, config = {}) {
    const requestURL = `${this.integrationsURL}/validate/partition-key`;

    const params = {
      partition_key: value.destinationPartitionKey
    };

    const options: NetworkRequestOptions = {
      uiOptions: BACKGROUND_NETWORK_REQ_UI_OPTIONS,
      networkOptions: {
        ...config
      }
    };

    return hermesCourierService.post<GenericApiResponse<{ valid: boolean; reason: string }>>(
      requestURL,
      params,
      options
    );
  },

  triggerPCSZap(formData: FormData) {
    const zapierPostHookUrl = 'https://hooks.zapier.com/hooks/catch/1862759/usgbrh4/';

    const reqOptions: NetworkRequestOptions = {
      uiOptions: BACKGROUND_NETWORK_REQ_UI_OPTIONS,
      networkOptions: {
        headers: {
          Anonymous: 'True'
        }
      }
    };

    return hermesCourierService.post(zapierPostHookUrl, formData, reqOptions);
  },

  disablePipeline(pipelineId: number, cancelRunningJobs: boolean) {
    return hermesCourierService.post<GenericApiResponse<RawIntegration>>(
      `${this.integrationsURL}/${pipelineId}/actions/disable`,
      {
        should_cancel_running_jobs: cancelRunningJobs
      }
    );
  },

  enablePipeline(pipelineId: number) {
    return hermesCourierService.post<GenericApiResponse<RawIntegration>>(
      `${this.integrationsURL}/${pipelineId}/actions/enable`,
      {}
    );
  },

  resyncPipeline(
    pipelineId: number,
    config: Partial<AxiosRequestConfig> = {},
    dropAndLoadEnabled?: boolean
  ) {
    const options: NetworkRequestOptions = {
      uiOptions: BACKGROUND_NETWORK_REQ_UI_OPTIONS,
      networkOptions: {
        ...config
      }
    };
    return hermesCourierService.post<GenericApiResponse<RawIntegration>>(
      `${this.integrationsURL}/${pipelineId}/actions/restart`,
      {
        destination_table_mode: dropAndLoadEnabled ? 'DROP_AND_LOAD' : 'EVOLVE'
      },
      options
    );
  },

  updateSyncFrequency(
    pipelineId: number,
    syncFrequency: number,
    config: Partial<AxiosRequestConfig> = {}
  ) {
    const options: NetworkRequestOptions = {
      uiOptions: BACKGROUND_NETWORK_REQ_UI_OPTIONS,
      networkOptions: {
        ...config
      }
    };

    return hermesCourierService.post<GenericApiResponse<RawIntegration>>(
      `${this.integrationsURL}/${pipelineId}/actions/update-sync-frequency`,
      {
        sync_frequency: syncFrequency
      },
      options
    );
  },

  runNow(pipelineId: number, config: Partial<AxiosRequestConfig> = {}) {
    const options: NetworkRequestOptions = {
      uiOptions: BACKGROUND_NETWORK_REQ_UI_OPTIONS,
      networkOptions: {
        ...config
      }
    };

    return hermesCourierService.put<GenericApiResponse<RawIntegration>>(
      `${this.integrationsURL}/${pipelineId}/actions/run-now`,
      {},
      options
    );
  },

  deletePipeline(pipelineId: number, config: Partial<AxiosRequestConfig> = {}) {
    const options: NetworkRequestOptions = {
      uiOptions: BACKGROUND_NETWORK_REQ_UI_OPTIONS,
      networkOptions: {
        ...config
      }
    };

    return hermesCourierService.delete<GenericApiResponse<RawIntegration>>(
      `${this.integrationsURL}/${pipelineId}`,
      options
    );
  },

  getIntegrationFeatures(pipelineId: number) {
    return hermesCourierService.get<GenericApiResponse<PipelineSettingsFeaturesData>>(
      `${this.integrationsURL}/${pipelineId}/features`
    );
  },

  getIntegrationDisplayConfig(pipelineId: number) {
    return hermesCourierService.get<
      GenericApiResponse<{
        source: RawEntityDisplayConfig<RawSourceType>;
        destination: RawEntityDisplayConfig<RawDestinationType>;
        integration: RawEntityDisplayConfig<RawIntegration>;
      }>
    >(`${this.integrationsURL}/${pipelineId}/display-config`);
  },

  editPipeline(pipelineId: number) {
    return hermesCourierService.post<GenericApiResponse<RawIntegration>>(
      `${this.integrationsURL}/${pipelineId}/actions/edit`,
      {}
    );
  },

  savePipeline(pipelineId: number) {
    return hermesCourierService.put<GenericApiResponse<unknown>>(
      `${this.integrationsURL}/${pipelineId}/actions/edit/save`,
      {},
      NETWORK_REQ_OPTIONS_WITH_TOAST
    );
  },

  cancelPipelineEditSave(pipelineId: number) {
    return hermesCourierService.put(
      `${this.integrationsURL}/${pipelineId}/actions/edit/cancel`,
      {},
      NETWORK_REQ_OPTIONS_WITH_TOAST
    );
  },

  getPipelineReviewContent(pipelineId: number) {
    return hermesCourierService.get<GenericApiResponse<RawPipelineReviewContent>>(
      `${this.integrationsURL}/${pipelineId}/actions/edit/review`
    );
  },

  getPipelineBanners(pipelineId: number, options = DEFAULT_NETWORK_REQ_OPTIONS) {
    return hermesCourierService.get<GenericApiResponse<RawIntegrationBannerResponse>>(
      `${this.integrationsURL}/${pipelineId}/banner`,
      options
    );
  },

  submitPipelineReviewSelectionDiffRequest(pipelineId: number) {
    return hermesCourierService.put<
      GenericApiResponse<{
        review_page_id: string;
      }>
    >(
      `${this.integrationsURL}/${pipelineId}/actions/edit/review/selections`,
      {},
      DEFAULT_NETWORK_REQ_OPTIONS
    );
  },

  getPipelineReviewSelection(pipelineId: number, reviewPageId: string) {
    return hermesCourierService.get<GenericApiResponse<RawPipelineReviewSelectionResponse>>(
      `${this.integrationsURL}/${pipelineId}/actions/edit/review/selections/${reviewPageId}/status`
    );
  },

  upgradePipeline(
    pipelineId: number,
    inVerifyMode: boolean,
    megatronProjectId?: number,
    megatronBranch?: string
  ) {
    const body: Record<string, unknown> = {
      standard_integration_id: pipelineId,
      is_premigration_validation: inVerifyMode
    };

    if (megatronProjectId) {
      body.megatron_project_id = megatronProjectId;
    }

    if (megatronBranch) {
      body.megatron_branch = megatronBranch;
    }

    return hermesCourierService.post<GenericApiResponse<RawIntegration & { connector_id: string; connector_version: string; connector_ingestion_mode: string, source_type: string }>>(
      `${this.integrationsURL}/create/migrate`,
      body
    );
  },

  getV1Pipeline(seqId: number, options = DEFAULT_NETWORK_REQ_OPTIONS) {
    const requestURL = `${this.upgradeIntegrationURL}/by-seq-id/${seqId}`;

    return hermesCourierService.get<GenericApiResponse<StandardPipeline>>(requestURL, options);
  }
};
